// Pure helpers for shop inventory and price lists. Shared by the server (validation) and the
// browser (column guessing and previews), so both agree on what a row means.

export const MAX_INVENTORY_ROWS = 20000;
export const LOW_STOCK_AT = 2;

/** Fields a shop can supply, in the order they appear in the upload mapper. */
export const INVENTORY_FIELDS = [
  { key: 'pn', label: 'Part number', required: true },
  { key: 'price', label: 'Your price' },
  { key: 'qty', label: 'Quantity on hand' },
  { key: 'sku', label: 'Your SKU' },
  { key: 'bin', label: 'Bin / location' },
  { key: 'cost', label: 'Your cost (private)' },
  { key: 'description', label: 'Description' },
];

const squash = (s) => String(s ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');

// Header names seen in common parts and point-of-sale exports, checked in order.
const HEADER_GUESSES = {
  pn: ['partnumber', 'partno', 'partnum', 'part', 'pn', 'mfrpart', 'mfrpn', 'mpn', 'oempn', 'oempartnumber', 'itemnumber', 'itemno', 'item'],
  price: ['price', 'yourprice', 'retail', 'retailprice', 'sellprice', 'sell', 'unitprice', 'listprice'],
  qty: ['qty', 'quantity', 'qtyonhand', 'onhand', 'stock', 'instock', 'available'],
  sku: ['sku', 'yoursku', 'shopsku', 'internalsku', 'stockno', 'stocknumber', 'internalpart'],
  bin: ['bin', 'binlocation', 'location', 'shelf', 'binno'],
  cost: ['cost', 'yourcost', 'unitcost', 'dealercost', 'avgcost', 'averagecost'],
  description: ['description', 'desc', 'descrip', 'name', 'itemdescription'],
};

/** Suggest which uploaded column feeds each field: { pn: 'Part #', price: 'Retail', ... }. */
export function guessColumns(headers) {
  const byKey = new Map(headers.map((h) => [squash(h), h]));
  const used = new Set();
  const out = {};
  for (const [field, names] of Object.entries(HEADER_GUESSES)) {
    const hit = names.find((n) => byKey.has(n) && !used.has(byKey.get(n)));
    if (hit) {
      out[field] = byKey.get(hit);
      used.add(byKey.get(hit));
    }
  }
  return out;
}

/** "$1,234.50" -> 1234.5; blank or unreadable -> null. */
export function parseMoney(value) {
  const cleaned = String(value ?? '').replace(/[^0-9.-]/g, '');
  if (cleaned === '' || cleaned === '-' || cleaned === '.') return null;
  const n = Number(cleaned);
  return Number.isFinite(n) && n >= 0 ? Math.round(n * 100) / 100 : null;
}

/** "12", "12.0", "1,200" -> whole number; blank or unreadable -> null (unknown, not zero). */
export function parseQty(value) {
  const cleaned = String(value ?? '').replace(/[^0-9.-]/g, '');
  if (cleaned === '' || cleaned === '-' || cleaned === '.') return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? Math.max(0, Math.round(n)) : null;
}

/** 'in' | 'low' | 'out' | 'unknown' for a quantity on hand. */
export function stockLevel(qty) {
  if (qty === null || qty === undefined || qty === '') return 'unknown';
  const n = Number(qty);
  if (!Number.isFinite(n)) return 'unknown';
  if (n <= 0) return 'out';
  return n <= LOW_STOCK_AT ? 'low' : 'in';
}

const clip = (value, max) => String(value ?? '').trim().slice(0, max);

/**
 * Turn one mapped upload row into a stored record, or return a reason it was skipped.
 * `normalise` is injected so this file stays free of imports.
 */
export function cleanInventoryRow(row, normalise) {
  const pn = clip(row.pn, 100);
  const pnKey = normalise(pn);
  if (!pnKey) return { skip: 'Missing part number' };
  return {
    record: {
      pn,
      pn_key: pnKey,
      description: clip(row.description, 200),
      price: parseMoney(row.price),
      cost: parseMoney(row.cost),
      qty: parseQty(row.qty),
      sku: clip(row.sku, 60),
      bin: clip(row.bin, 40),
    },
  };
}

export const formatPrice = (price) =>
  price === null || price === undefined || price === '' || !Number.isFinite(Number(price))
    ? ''
    : Number(price).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
